import { useState, useEffect } from 'react';
import { Accordion, AccordionDetails, FormControlLabel, Checkbox, Tooltip, Button, IconButton } from '@mui/material';
import AddShoppingCartIcon from '@mui/icons-material/AddShoppingCart';
import CloseIcon from '@mui/icons-material/Close';
import { daysOfWeek } from '../../constants';
import { PricingDetail } from '../../types';

interface ScheduleSelectorProps {
  pricing: PricingDetail;
  expanded: boolean;
  setExpanded: (value: boolean) => void;
  onAddToCart: (selectedDays: string[]) => void;
  /** When true, a cart action is in progress and the button should show loading state. */
  isPricingUpdating?: boolean;
}

const ScheduleSelector = ({
  pricing,
  expanded,
  setExpanded,
  onAddToCart,
  isPricingUpdating = false,
}: ScheduleSelectorProps) => {
  const readOnly = !pricing.isSelectScheduleAllowed;
  const [selectedDays, setSelectedDays] = useState<Record<string, boolean>>({});
  const [addToCartDisabled, setAddToCartDisabled] = useState(!readOnly);

  useEffect(() => {
    if (readOnly) return;

    const hasSelectedDay = Object.values(selectedDays).some((value) => value);
    if (!hasSelectedDay && !addToCartDisabled) setAddToCartDisabled(true);
    else if (hasSelectedDay && addToCartDisabled) setAddToCartDisabled(false);
  }, [selectedDays, readOnly]);

  const selectedCount = Object.values(selectedDays).filter(Boolean).length;
  const maxScheduleDays = pricing.maxScheduleDays;
  const isMaxSelectionReached = maxScheduleDays > 0 && selectedCount >= maxScheduleDays;

  const handleCheckboxChange = (day: string, isChecked: boolean) => {
    if (isChecked && isMaxSelectionReached) return;

    setSelectedDays({ ...selectedDays, [day]: isChecked });
  };

  const isDayCheckboxDisabled = (day: string) => {
    if (readOnly || !pricing[day as keyof PricingDetail]) return true;
    if (isMaxSelectionReached && !selectedDays[day]) return true;
    return false;
  };

  const handleAddToCartClick = () => {
    if (readOnly) {
      onAddToCart([]);
      return;
    }

    const selectedDaysArray = Object.entries(selectedDays)
      .filter(([_key, value]) => value)
      .map(([key]) => key);
    onAddToCart(selectedDaysArray);
  };

  const isButtonDisabled = readOnly ? isPricingUpdating : addToCartDisabled || isPricingUpdating;
  const showAddToCartButton = readOnly ? !isPricingUpdating : !isButtonDisabled;
  const tooltipTitle = addToCartDisabled
    ? 'Please select at least 1 day'
    : isPricingUpdating
      ? 'Processing...'
      : readOnly
        ? 'Add to Cart'
        : 'Add selected days to Cart';

  return (
    <Accordion
      expanded={expanded}
      onChange={(_event, isExpanded) => setExpanded(isExpanded)}
      className="schedule-selector ptf-0 border-bottom-none"
    >
      <AccordionDetails className="">
        <div className="content-item" style={{ display: 'flex', justifyContent: 'center', marginBottom: 8 }}>
          <Button className="cursor-pointer btn-add-cart" onClick={() => setExpanded(false)}>
            <Tooltip title="Close" className="pt-0 btn-cart">
              <IconButton className="ptf-0 plf-0 ptb-0">
                <CloseIcon />
              </IconButton>
            </Tooltip>
            Close
          </Button>
        </div>
        <p className="p-info text-alignment pb-1">
          {readOnly ? (
            'Included Days'
          ) : (
            <>
              Choose Days <b>(MAX: {pricing.maxScheduleDays})</b>
            </>
          )}
        </p>
        <div className="text-center content-item">
          {Object.entries(daysOfWeek).map(([key, fullName]) => (
            <FormControlLabel
              key={key}
              control={
                <Checkbox
                  checked={readOnly ? !!pricing[key as keyof PricingDetail] : !!selectedDays[key]}
                  disabled={isDayCheckboxDisabled(key)}
                  onChange={(e) => handleCheckboxChange(key, e.target.checked)}
                  sx={{
                    paddingRight: '1px !important',
                  }}
                />
              }
              label={fullName.charAt(0).toUpperCase()}
              sx={{
                marginRight: '10px !important',
              }}
            />
          ))}
          {showAddToCartButton && (
            <Tooltip title={tooltipTitle} className="pt-3 btn-cart">
              <span>
                <Button
                  className="cursor-pointer p-14 btn-add-cart"
                  onClick={handleAddToCartClick}
                  disabled={isButtonDisabled}
                  startIcon={<AddShoppingCartIcon />}
                  sx={{ minWidth: 170 }}
                >
                  {isPricingUpdating ? 'LOADING' : 'Add to cart'}
                </Button>
              </span>
            </Tooltip>
          )}
        </div>
      </AccordionDetails>
    </Accordion>
  );
};

export default ScheduleSelector;
